import { useCallback, useEffect, useMemo, useState } from "react";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';
import {
  FiArrowUpRight,
  FiDollarSign,
  FiPlus,
  FiRefreshCw,
  FiSearch,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "—";

const statusClass = {
  OPEN: "bg-amber-50 text-amber-700 ring-amber-200",
  CLOSED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  CANCELLED: "bg-gray-100 text-gray-600 ring-gray-200",
};

export default function EmployeeAdvancesPage() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("OPEN");
  const [allAdvances, setAdvances] = useState([]);
  const [hrmsFilters, setHrmsFilters] = useState({});
  const advances = useMemo(() => allAdvances.filter(row => matchesHrmsFilters(row,hrmsFilters)), [allAdvances,hrmsFilters]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAdvances = useCallback(async () => {
    if (!accessToken) return;

    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (status !== "ALL") params.set("status", status);

    try {
      setLoading(true);
      setError("");
      const result = await apiRequest(`/employee-advances?${params}`, {
        accessToken,
      });
      setAdvances(Array.isArray(result) ? result : result?.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load employee advances.");
      setAdvances([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, search, status]);

  useEffect(() => {
    const timer = setTimeout(loadAdvances, 250);
    return () => clearTimeout(timer);
  }, [loadAdvances]);

  const summary = useMemo(
    () => ({
      issued: advances.reduce(
        (total, advance) => total + Number(advance.issuedAmount || 0),
        0,
      ),
      recovered: advances.reduce(
        (total, advance) => total + Number(advance.recoveredAmount || 0),
        0,
      ),
      outstanding: advances.reduce(
        (total, advance) => total + Number(advance.outstandingAmount || 0),
        0,
      ),
      openCount: advances.filter((advance) => advance.status === "OPEN").length,
    }),
    [advances],
  );

  return (
    <div className="space-y-6 pb-10">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-950">
            Employee Advance Payments
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
            A complete register of money advanced to employees and the balance
            still to be recovered.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/admindashboard/employee-advances/new")}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-900 focus:outline-none focus:ring-4 focus:ring-primary-100"
        >
          <FiPlus size={18} />
          Add advance
        </button>
      </header>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Open advances" value={summary.openCount} />
        <SummaryCard label="Amount issued" value={money(summary.issued)} />
        <SummaryCard
          label="Amount recovered"
          value={money(summary.recovered)}
          tone="good"
        />
        <SummaryCard
          label="Outstanding balance"
          value={money(summary.outstanding)}
          tone="attention"
        />
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 p-4 lg:flex-row lg:items-center lg:justify-between lg:p-5">
          <div className="relative w-full lg:max-w-md">
            <FiSearch
              className="absolute left-3.5 top-3 text-gray-400"
              size={18}
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search employee name or employee code"
              className="min-h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm outline-none transition placeholder:text-gray-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="min-h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
            >
              <option value="OPEN">Open only</option>
              <option value="CLOSED">Closed only</option>
              <option value="CANCELLED">Cancelled only</option>
              <option value="ALL">All statuses</option>
            </select>
            <button
              type="button"
              title="Refresh advances"
              onClick={loadAdvances}
              className="grid h-11 w-11 place-items-center rounded-xl border border-gray-200 text-gray-600 transition hover:bg-gray-50"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="p-5"><HrmsFilters records={allAdvances} value={hrmsFilters} onChange={setHrmsFilters} /></div>
        <div className="overflow-x-auto">
          <table className="min-w-[920px] w-full text-left">
            <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-5 py-3.5">Employee</th>
                <th className="px-5 py-3.5">Issued on</th>
                <th className="px-5 py-3.5 text-right">Issued</th>
                <th className="px-5 py-3.5 text-right">Recovered</th>
                <th className="px-5 py-3.5 text-right">Outstanding</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-5 py-14 text-center text-sm text-gray-500"
                  >
                    Loading advances…
                  </td>
                </tr>
              ) : advances.length ? (
                advances.map((advance) => (
                  <tr
                    key={advance.id}
                    className="transition hover:bg-gray-50/80"
                  >
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-900">
                        {advance.employee?.fullName || "Unknown employee"}
                      </p>
                      <p className="mt-0.5 text-xs font-medium text-gray-500">
                        {advance.employee?.employeeCode || "—"}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      <p>{formatDate(advance.issuedOn)}</p>
                      <p
                        className="mt-0.5 max-w-[230px] truncate text-xs text-gray-400"
                        title={advance.reason}
                      >
                        {advance.reason}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-right text-sm font-medium text-gray-900">
                      {money(advance.issuedAmount)}
                    </td>
                    <td className="px-5 py-4 text-right text-sm font-medium text-emerald-700">
                      {money(advance.recoveredAmount)}
                    </td>
                    <td className="px-5 py-4 text-right text-sm font-bold text-gray-950">
                      {money(advance.outstandingAmount)}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset ${statusClass[advance.status] || statusClass.CANCELLED}`}
                      >
                        {advance.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/admindashboard/employee-advances/${advance.id}`,
                          )
                        }
                        className="inline-flex items-center gap-1 text-sm font-semibold text-primary-800 transition hover:text-primary-950"
                      >
                        View <FiArrowUpRight />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-5 py-16 text-center">
                    <FiDollarSign
                      className="mx-auto mb-3 text-gray-300"
                      size={30}
                    />
                    <p className="font-semibold text-gray-700">
                      No advances found
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Change the filters or add the first employee advance.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function SummaryCard({ label, value, tone = "default" }) {
  const toneClass = {
    default: "border-gray-200 bg-white text-gray-950",
    good: "border-emerald-100 bg-emerald-50/40 text-emerald-800",
    attention: "border-amber-100 bg-amber-50/40 text-amber-900",
  };

  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${toneClass[tone]}`}>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-2 text-2xl font-bold tracking-tight">{value}</p>
    </div>
  );
}
